import type { EChartsType } from 'echarts/core';

import type { TimeSeries } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';

import { TOOLTIP_ANCHOR_SERIES_ID } from '../../config/componentIds';

export type SeriesLookup = {
  /** ECharts series index → neutral series, for the line series only. */
  seriesByIndex: Map<number, TimeSeries>;
  /** ECharts index of the hidden tooltip anchor series, when the option has one. */
  anchorSeriesIndex: number | undefined;
};

/** replaceMerge keeps component indexes stable, so getOption() holds null in place of a removed series. */
function getSeriesOptionId(seriesOption: unknown): string | undefined {
  if (typeof seriesOption !== 'object' || seriesOption === null || !('id' in seriesOption)) {
    return undefined;
  }

  const { id } = seriesOption;

  return typeof id === 'string' || typeof id === 'number' ? String(id) : undefined;
}

/** One `getOption()` per registration: it clones the whole option, so it must not run per event. */
export function createSeriesLookup(chart: EChartsType, series: TimeSeries[]): SeriesLookup {
  const seriesById = new Map(series.map((timeSeries) => [timeSeries.id, timeSeries]));
  const mappedSeriesIds = new Set<string>();

  const seriesByIndex = new Map<number, TimeSeries>();
  let anchorSeriesIndex: number | undefined;
  // getOption() is typed as `any` below the top level, so the series list is narrowed by hand.
  const chartSeries: unknown = chart.getOption().series;

  if (!Array.isArray(chartSeries)) {
    return { seriesByIndex, anchorSeriesIndex };
  }

  chartSeries.forEach((seriesOption: unknown, seriesIndex) => {
    const id = getSeriesOptionId(seriesOption);

    if (id === undefined) {
      return;
    }

    if (id === TOOLTIP_ANCHOR_SERIES_ID) {
      anchorSeriesIndex = seriesIndex;
      return;
    }

    const timeSeries = seriesById.get(id);

    if (!timeSeries || mappedSeriesIds.has(timeSeries.id)) {
      return;
    }

    mappedSeriesIds.add(timeSeries.id);
    seriesByIndex.set(seriesIndex, timeSeries);
  });

  return { seriesByIndex, anchorSeriesIndex };
}
